import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { useMemo, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";

import {
  borrarGarantia,
  borrarPrestamoPant3ra,
  crearGarantia,
  crearPrestamoPant3ra,
  editarGarantia,
  editarPrestamoPant3ra,
  getGarantias,
  type Garantia,
  type GarantiaIn,
  type PrestamoPant3raIn,
} from "@/lib/api";

const num = (n: number) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fechaCorta = (iso: string | null) => {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const aNumero = (s: string) => Number(s.replace(/[$,\s]/g, ""));
const hoy = () => new Date().toLocaleDateString("en-CA");

/** Días de hoy al vencimiento (negativo = ya venció). */
const diasA = (iso: string | null) =>
  iso == null ? null : Math.round((new Date(`${iso}T00:00:00`).getTime() - new Date(`${hoy()}T00:00:00`).getTime()) / 86_400_000);

const NUEVA = "__nueva__";
const NUEVO = -1;

type BorradorG = Omit<GarantiaIn, "monto"> & { monto: string };
type BorradorP = Omit<PrestamoPant3raIn, "monto"> & { monto: string };

/*
 * Operación → Contabilidad → Garantías y préstamos. Registro maestro de las
 * garantías (colocadas por MPP y recibidas de clientes) y del préstamo Pant3ra;
 * reemplaza las pestañas "Garantías" y "Resumen" del Archivo Maestro y alimenta
 * el Reporte de Caja.
 */
export function GarantiasPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["garantias"], queryFn: getGarantias });
  const [error, setError] = useState<string | null>(null);
  const [editG, setEditG] = useState<string | null>(null);
  const [bG, setBG] = useState<BorradorG | null>(null);
  const [editP, setEditP] = useState<number | null>(null);
  const [bP, setBP] = useState<BorradorP | null>(null);

  const tc = q.data?.tipo_cambio.mxn_por_usd ?? 0;
  const invalidar = () => qc.invalidateQueries({ queryKey: ["garantias"] });
  const err = (e: unknown, def: string) => setError(e instanceof Error ? e.message : def);

  const guardarG = useMutation({
    mutationFn: ({ id, b }: { id: string; b: BorradorG }) => {
      const g: GarantiaIn = {
        ...b,
        monto: aNumero(b.monto),
        fecha_inicio: b.fecha_inicio || null,
        fecha_vencimiento: b.fecha_vencimiento || null,
      };
      return id === NUEVA ? crearGarantia(g) : editarGarantia(id, g);
    },
    onSuccess: () => { setError(null); setEditG(null); setBG(null); invalidar(); },
    onError: (e) => err(e, "No se pudo guardar la garantía"),
  });
  const borrarG = useMutation({
    mutationFn: borrarGarantia,
    onSuccess: invalidar,
    onError: (e) => err(e, "No se pudo borrar"),
  });

  const guardarP = useMutation({
    mutationFn: ({ id, b }: { id: number; b: BorradorP }) => {
      const p: PrestamoPant3raIn = { ...b, monto: aNumero(b.monto) };
      return id === NUEVO ? crearPrestamoPant3ra(p) : editarPrestamoPant3ra(id, p);
    },
    onSuccess: () => { setError(null); setEditP(null); setBP(null); invalidar(); },
    onError: (e) => err(e, "No se pudo guardar el movimiento"),
  });
  const borrarP = useMutation({
    mutationFn: borrarPrestamoPant3ra,
    onSuccess: invalidar,
    onError: (e) => err(e, "No se pudo borrar"),
  });

  const garantias = q.data?.garantias ?? [];
  const prestamos = useMemo(() => q.data?.prestamos ?? [], [q.data]);

  // Saldo deudor del préstamo después de cada movimiento, en MXN eq.
  const filasP = useMemo(() => {
    let saldo = 0;
    return prestamos.map((p) => {
      saldo += (p.tipo === "Préstamo" ? 1 : -1) * p.monto * (p.moneda === "USD" ? tc : 1);
      return { p, saldo };
    });
  }, [prestamos, tc]);
  const saldoP = filasP.length ? filasP[filasP.length - 1].saldo : 0;

  const totalDir = (dir: Garantia["direccion"]) =>
    garantias
      .filter((g) => g.direccion === dir && g.activa)
      .reduce((a, g) => a + g.monto * (g.moneda === "USD" ? tc : 1), 0);

  const nuevaG = (direccion: Garantia["direccion"]) => {
    const n = garantias.length + 1;
    setEditG(NUEVA);
    setBG({
      id_garantia: `GR-${String(n).padStart(3, "0")}`, direccion, contraparte: "", tipo: "", categoria: "",
      concepto: "", monto: "", moneda: "MXN", fecha_inicio: hoy(), fecha_vencimiento: null, vigencia: "", activa: true,
    });
  };
  const editarFilaG = (g: Garantia) => {
    setEditG(g.id_garantia);
    setBG({ ...g, monto: String(g.monto) });
  };

  const tablaGarantias = (dir: Garantia["direccion"], titulo: string, nota: string) => {
    const filas = garantias.filter((g) => g.direccion === dir);
    return (
      <div className="bg-white border rounded-lg overflow-x-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="font-semibold text-slate-800">{titulo}</div>
            <div className="text-xs text-slate-500">{nota}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-slate-500">Total vigente (MXN eq)</div>
              <div className="font-semibold tabular-nums">{num(totalDir(dir))}</div>
            </div>
            <button
              type="button"
              onClick={() => nuevaG(dir)}
              disabled={editG !== null}
              className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Agregar
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left text-xs">
            <tr>
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Contraparte</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Categoría</th>
              <th className="px-3 py-2">Concepto / OC</th>
              <th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2">Inicio</th>
              <th className="px-3 py-2">Vencimiento / vigencia</th>
              <th className="px-3 py-2">Vigente</th>
              <th className="px-3 py-2 w-16" />
            </tr>
          </thead>
          <tbody>
            {editG === NUEVA && bG?.direccion === dir && (
              <FilaGarantia b={bG} onChange={setBG} ocupado={guardarG.isPending}
                onGuardar={() => guardarG.mutate({ id: NUEVA, b: bG })} onCancelar={() => { setEditG(null); setBG(null); }} />
            )}
            {filas.map((g) => {
              if (editG === g.id_garantia && bG) {
                return (
                  <FilaGarantia key={g.id_garantia} b={bG} onChange={setBG} ocupado={guardarG.isPending}
                    onGuardar={() => guardarG.mutate({ id: g.id_garantia, b: bG })} onCancelar={() => { setEditG(null); setBG(null); }} />
                );
              }
              const dias = diasA(g.fecha_vencimiento);
              const critica = g.activa && dias != null && dias < 30;
              return (
                <tr key={g.id_garantia} className={`border-t hover:bg-slate-50 ${g.activa ? "" : "text-slate-400"}`}>
                  <td className="px-3 py-1.5 font-medium whitespace-nowrap">{g.id_garantia}</td>
                  <td className="px-3 py-1.5">{g.contraparte}</td>
                  <td className="px-3 py-1.5">{g.tipo ?? "—"}</td>
                  <td className="px-3 py-1.5">{g.categoria ?? "—"}</td>
                  <td className="px-3 py-1.5">{g.concepto ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{num(g.monto)} {g.moneda}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fechaCorta(g.fecha_inicio)}</td>
                  <td className={`px-3 py-1.5 ${critica ? "text-red-600 font-semibold" : ""}`}>
                    {g.fecha_vencimiento ? fechaCorta(g.fecha_vencimiento) : g.vigencia ?? "—"}
                    {critica && <span className="text-[11px]"> · {dias! < 0 ? "vencida" : `${dias} días`}</span>}
                  </td>
                  <td className="px-3 py-1.5">{g.activa ? "Sí" : "Liberada"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <button type="button" title="Modificar" disabled={editG !== null} onClick={() => editarFilaG(g)}
                      className="text-slate-400 hover:text-blue-600 disabled:opacity-30 mr-2">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" title="Eliminar" disabled={editG !== null}
                      onClick={() => { if (window.confirm(`¿Eliminar la garantía ${g.id_garantia} (${g.contraparte})?`)) borrarG.mutate(g.id_garantia); }}
                      className="text-slate-400 hover:text-red-600 disabled:opacity-30">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filas.length === 0 && !(editG === NUEVA && bG?.direccion === dir) && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">Sin garantías.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-slate-500" /> Garantías y préstamos
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Registro de las garantías que MPP coloca y las que recibe de clientes, y de los movimientos del préstamo
          Pant3ra. Se muestran en el <Link to="/reportes/caja" className="text-blue-600 hover:underline">Reporte de Caja</Link>.
          Los USD se convierten con el{" "}
          <Link to="/tipo-cambio" className="text-blue-600 hover:underline">TC {tc.toFixed(2)}</Link>. En rojo, las garantías
          vigentes que vencen en menos de 30 días.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm flex justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      {q.isLoading && <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>}
      {q.isError && <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">{(q.error as Error).message}</div>}

      {q.data && (
        <>
          {tablaGarantias("colocada", "Garantías colocadas", "Las pone MPP: CENACE, Iberdrola…")}
          {tablaGarantias("recibida", "Garantías recibidas de clientes", "Respaldo a favor de MPP: SBLC, prepagos…")}

          <div className="bg-white border rounded-lg overflow-x-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <div className="font-semibold text-slate-800">Préstamos Pant3ra</div>
                <div className="text-xs text-slate-500">Préstamo suma al saldo deudor; Pago lo resta.</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-slate-500">Saldo deudor (MXN eq)</div>
                  <div className="font-semibold tabular-nums">{num(saldoP)}</div>
                </div>
                <button
                  type="button"
                  disabled={editP !== null}
                  onClick={() => { setEditP(NUEVO); setBP({ fecha: hoy(), concepto: "", tipo: "Préstamo", monto: "", moneda: "MXN" }); }}
                  className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" /> Agregar
                </button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left text-xs">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Concepto</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-right">Saldo deudor (MXN eq)</th>
                  <th className="px-3 py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {editP === NUEVO && bP && (
                  <FilaPrestamo b={bP} onChange={setBP} ocupado={guardarP.isPending}
                    onGuardar={() => guardarP.mutate({ id: NUEVO, b: bP })} onCancelar={() => { setEditP(null); setBP(null); }} />
                )}
                {filasP.map(({ p, saldo }) =>
                  editP === p.id && bP ? (
                    <FilaPrestamo key={p.id} b={bP} onChange={setBP} ocupado={guardarP.isPending}
                      onGuardar={() => guardarP.mutate({ id: p.id, b: bP })} onCancelar={() => { setEditP(null); setBP(null); }} />
                  ) : (
                    <tr key={p.id} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fechaCorta(p.fecha)}</td>
                      <td className="px-3 py-1.5">{p.concepto ?? "—"}</td>
                      <td className="px-3 py-1.5">{p.tipo}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(p.monto)} {p.moneda}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{num(saldo)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <button type="button" title="Modificar" disabled={editP !== null}
                          onClick={() => { setEditP(p.id); setBP({ fecha: p.fecha, concepto: p.concepto ?? "", tipo: p.tipo, monto: String(p.monto), moneda: p.moneda }); }}
                          className="text-slate-400 hover:text-blue-600 disabled:opacity-30 mr-2">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button type="button" title="Eliminar" disabled={editP !== null}
                          onClick={() => { if (window.confirm(`¿Eliminar el movimiento del ${fechaCorta(p.fecha)}?`)) borrarP.mutate(p.id); }}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ),
                )}
                {prestamos.length === 0 && editP !== NUEVO && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Sin movimientos: el saldo deudor es 0.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const inp = "border rounded px-1.5 py-1 text-sm w-full";

function Acciones({ ocupado, onGuardar, onCancelar }: { ocupado: boolean; onGuardar: () => void; onCancelar: () => void }) {
  return (
    <td className="px-2 py-1.5 whitespace-nowrap">
      <button type="button" title="Guardar (Enter)" disabled={ocupado} onClick={onGuardar} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 mr-2">
        <Check className="w-4 h-4" />
      </button>
      <button type="button" title="Cancelar (Esc)" onClick={onCancelar} className="text-slate-400 hover:text-slate-700">
        <X className="w-4 h-4" />
      </button>
    </td>
  );
}

const teclas = (onGuardar: () => void, onCancelar: () => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter") onGuardar();
  if (e.key === "Escape") onCancelar();
};

function FilaGarantia({ b, onChange, ocupado, onGuardar, onCancelar }: {
  b: BorradorG; onChange: (b: BorradorG) => void; ocupado: boolean; onGuardar: () => void; onCancelar: () => void;
}) {
  const set = <K extends keyof BorradorG>(k: K, v: BorradorG[K]) => onChange({ ...b, [k]: v });
  return (
    <tr className="border-t bg-blue-50/60 align-top" onKeyDown={teclas(onGuardar, onCancelar)}>
      <td className="px-2 py-1.5 w-24"><input value={b.id_garantia} onChange={(e) => set("id_garantia", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[120px]"><input autoFocus value={b.contraparte} onChange={(e) => set("contraparte", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5"><input value={b.tipo ?? ""} placeholder="Efectivo / SBLC" onChange={(e) => set("tipo", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5"><input value={b.categoria ?? ""} onChange={(e) => set("categoria", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5"><input value={b.concepto ?? ""} onChange={(e) => set("concepto", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5">
        <div className="flex gap-1">
          <input inputMode="decimal" value={b.monto} placeholder="Monto" onChange={(e) => set("monto", e.target.value)} className={`${inp} text-right w-28`} />
          <select value={b.moneda} onChange={(e) => set("moneda", e.target.value as "MXN" | "USD")} className="border rounded px-1 text-sm">
            <option>MXN</option><option>USD</option>
          </select>
        </div>
      </td>
      <td className="px-2 py-1.5"><input type="date" value={b.fecha_inicio ?? ""} onChange={(e) => set("fecha_inicio", e.target.value || null)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[160px] space-y-1">
        <input type="date" value={b.fecha_vencimiento ?? ""} onChange={(e) => set("fecha_vencimiento", e.target.value || null)} className={inp} />
        <input value={b.vigencia ?? ""} placeholder="o vigencia en texto" onChange={(e) => set("vigencia", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <select value={b.direccion} onChange={(e) => set("direccion", e.target.value as BorradorG["direccion"])} className="border rounded px-1 text-sm mb-1 w-full">
          <option value="colocada">Colocada</option><option value="recibida">Recibida</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={b.activa} onChange={(e) => set("activa", e.target.checked)} /> Vigente
        </label>
      </td>
      <Acciones ocupado={ocupado} onGuardar={onGuardar} onCancelar={onCancelar} />
    </tr>
  );
}

function FilaPrestamo({ b, onChange, ocupado, onGuardar, onCancelar }: {
  b: BorradorP; onChange: (b: BorradorP) => void; ocupado: boolean; onGuardar: () => void; onCancelar: () => void;
}) {
  const set = <K extends keyof BorradorP>(k: K, v: BorradorP[K]) => onChange({ ...b, [k]: v });
  return (
    <tr className="border-t bg-blue-50/60" onKeyDown={teclas(onGuardar, onCancelar)}>
      <td className="px-2 py-1.5"><input type="date" value={b.fecha} onChange={(e) => set("fecha", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[220px]"><input autoFocus value={b.concepto ?? ""} onChange={(e) => set("concepto", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5">
        <select value={b.tipo} onChange={(e) => set("tipo", e.target.value as BorradorP["tipo"])} className={inp}>
          <option>Préstamo</option><option>Pago</option>
        </select>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex gap-1">
          <input inputMode="decimal" value={b.monto} placeholder="Monto" onChange={(e) => set("monto", e.target.value)} className={`${inp} text-right`} />
          <select value={b.moneda} onChange={(e) => set("moneda", e.target.value as "MXN" | "USD")} className="border rounded px-1 text-sm">
            <option>MXN</option><option>USD</option>
          </select>
        </div>
      </td>
      <td className="px-3 py-1.5 text-right text-xs text-slate-400">se recalcula al guardar</td>
      <Acciones ocupado={ocupado} onGuardar={onGuardar} onCancelar={onCancelar} />
    </tr>
  );
}

